import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import {
  ArrowRight,
  Gift,
  Users,
  Shield,
  Clock,
  CheckCircle,
  Star,
  Smartphone,
  Award,
  Heart,
  Globe,
  Zap,
  FileText,
  Lock,
  Eye,
  DollarSign,
} from "lucide-react";
import { useEffect, useState } from "react";

const VerifiedPollostersLanding = () => {
  const [pollsterCount, setPollsterCount] = useState(124567);

  useEffect(() => {
    document.title =
      "Become a NationCard™ Verified Pollster - Earn Rewards for Surveys";

    // Simulate live counter increment
    const interval = setInterval(() => {
      setPollsterCount((prev) => prev + Math.floor(Math.random() * 3));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const rewards = [
    { name: "Amazon Gift Cards", points: 100, value: "$5", icon: Gift },
    { name: "Coffee Shop Credits", points: 80, value: "$4", icon: Award },
    { name: "Charity Donations", points: 50, value: "$2.50", icon: Heart },
    { name: "Streaming Discounts", points: 120, value: "$6", icon: Star },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Sticky Register Button for Mobile */}
      <div className="fixed bottom-4 left-4 right-4 z-50 md:hidden">
        <Button
          size="lg"
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold shadow-xl"
          onClick={() => (window.location.href = "/verification-process")}
        >
          Register Now - Start Earning
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden bg-gradient-to-br from-amber-400 via-orange-500 to-amber-600">
        {/* Background overlay with hero image */}
        <div className="absolute inset-0 opacity-20 bg-[url('/img/nationcard-hero1.png')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-amber-400/90 via-orange-500/85 to-amber-600/90"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="mb-6 inline-flex items-center">
                  <Badge
                    variant="outline"
                    className="bg-white/10 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm"
                  >
                    <Gift size={16} className="mr-2" />
                    <span>Earn Real Rewards</span>
                  </Badge>
                </div>

                <div className="space-y-8">
                  <div className="space-y-6">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                      Become a{" "}
                      <span className="text-yellow-200">NationCard™</span>{" "}
                      Verified <span className="text-orange-200">Pollster</span>
                    </h1>
                    <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
                      Share your opinions, earn points, and redeem rewards with
                      our trusted civic-intel network.
                    </p>
                    <p className="text-lg md:text-xl opacity-80 leading-relaxed text-yellow-200">
                      Quick surveys, real rewards, meaningful impact on
                      decisions that matter
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      size="lg"
                      className="text-lg px-8 py-4 bg-white text-orange-600 hover:bg-gray-100 h-auto font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                      onClick={() =>
                        (window.location.href = "/verification-process")
                      }
                    >
                      Register Now
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="text-lg px-8 py-4 border-white/30 text-white hover:bg-white/10 hover:text-white h-auto backdrop-blur-sm hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                    >
                      See Rewards
                    </Button>
                  </div>

                  {/* Trust Indicators */}
                  <div className="flex flex-wrap items-center gap-4 text-sm opacity-80">
                    <div className="flex items-center gap-2">
                      <Users size={16} />
                      <span>{pollsterCount.toLocaleString()}+ Pollsters</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Gift size={16} />
                      <span>Real Rewards</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      <span>2-Min Surveys</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block relative">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <div className="space-y-4">
                    {/* Mock Phone Interface */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                          <Smartphone size={16} className="text-white" />
                        </div>
                        <div>
                          <div className="text-white font-semibold">
                            Your Dashboard
                          </div>
                          <div className="text-white/60 text-sm">
                            Pollster Hub
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                        <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse delay-150"></div>
                        <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse delay-300"></div>
                      </div>
                    </div>

                    {/* Mock Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          Your Points
                        </div>
                        <div className="text-yellow-400 text-2xl font-bold">
                          1,247
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                          <div className="bg-yellow-400 h-2 rounded-full w-3/4 animate-pulse"></div>
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          Surveys Done
                        </div>
                        <div className="text-orange-400 text-2xl font-bold">
                          23
                        </div>
                        <div className="text-white/60 text-xs mt-1">
                          This month
                        </div>
                      </div>
                    </div>

                    {/* Activity Feed */}
                    <div className="space-y-2">
                      <div className="text-white/80 text-xs">
                        Recent Activity
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          <span>Climate poll +50 points</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                          <span>Economic survey +40 points</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                          <span>Reward redeemed -100 points</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating points indicator */}
                <div className="absolute -top-4 -right-4 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="text-white text-xs">Next Reward</div>
                  <div className="text-yellow-300 font-bold">53 pts</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Become a Pollster Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Why Become a Pollster?
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Join thousands of people making their voices heard while earning
                real rewards
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center hover:light-shadow-elevated transition-all duration-300 group hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <div className="h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Users className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">
                    Make Your Voice Count
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Inform real decisions made by governments, brands, and media
                    organizations worldwide
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <div className="h-16 w-16 bg-gradient-to-br from-amber-100 to-orange-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Gift className="h-8 w-8 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">
                    Earn Points for Every Survey
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Get rewarded for your time with points you can redeem for
                    gift cards and partner perks
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <div className="h-16 w-16 bg-gradient-to-br from-green-100 to-emerald-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Globe className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">
                    Contribute to Important Research
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Your responses power research used by governments, brands,
                    and media to make better decisions
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <div className="h-16 w-16 bg-gradient-to-br from-purple-100 to-violet-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Clock className="h-8 w-8 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">
                    Quick & Mobile-Friendly
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Complete surveys in under 2 minutes on any device, wherever
                    you are
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                How It Works
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Get started in minutes and begin earning rewards for your
                valuable opinions
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="group hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <div className="absolute -top-4 left-6 w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  1
                </div>
                <CardHeader className="pt-8">
                  <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                    <CheckCircle className="h-8 w-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-xl">Sign Up & Verify</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Create your free account and confirm your identity in
                    minutes. Your verification ensures research integrity and
                    unlocks higher-paying surveys.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <div className="absolute -top-4 left-6 w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  2
                </div>
                <CardHeader className="pt-8">
                  <div className="h-16 w-16 bg-orange-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                    <Smartphone className="h-8 w-8 text-orange-600" />
                  </div>
                  <CardTitle className="text-xl">Take Quick Surveys</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Receive mobile alerts for surveys on topics you care about.
                    Share your feedback in under 2 minutes and make your voice
                    heard.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <div className="absolute -top-4 left-6 w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  3
                </div>
                <CardHeader className="pt-8">
                  <div className="h-16 w-16 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
                    <Gift className="h-8 w-8 text-green-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Earn & Redeem Points
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Watch your points balance grow with every survey. Redeem for
                    gift cards, donations to charities, or exclusive partner
                    perks.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Rewards Showcase Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Your Rewards
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Real rewards for real opinions. Choose from our growing catalog
                of partner perks.
              </p>

              {/* Points Conversion */}
              <div className="inline-flex items-center bg-gradient-to-r from-amber-100 to-orange-100 px-6 py-3 rounded-full">
                <DollarSign className="h-5 w-5 text-orange-600 mr-2" />
                <span className="font-semibold text-orange-700">
                  100 points = $5 in rewards
                </span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {rewards.map((reward, index) => (
                <Card
                  key={index}
                  className="group hover:light-shadow-elevated transition-all duration-300 hover:scale-105"
                >
                  <CardContent className="p-6 text-center">
                    <div className="h-16 w-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <reward.icon className="h-8 w-8 text-orange-600" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">
                      {reward.name}
                    </h3>
                    <div className="text-2xl font-bold text-orange-600 mb-1">
                      {reward.points} pts
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {reward.value} value
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Security Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-8">
                Trust & Security
              </h2>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="flex items-center space-x-3 bg-white p-6 rounded-lg light-shadow-base hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                  <Shield className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-semibold">Data Security</div>
                    <div className="text-sm text-muted-foreground">
                      Your data is anonymized & secure
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3 bg-white p-6 rounded-lg light-shadow-base hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                  <CheckCircle className="h-8 w-8 text-blue-600 flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-semibold">Verified Identity</div>
                    <div className="text-sm text-muted-foreground">
                      Research integrity through verification
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3 bg-white p-6 rounded-lg light-shadow-base hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                  <Eye className="h-8 w-8 text-purple-600 flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-semibold">Zero Spam</div>
                    <div className="text-sm text-muted-foreground">
                      Only relevant surveys for you
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mt-8">
                We comply with GDPR & local privacy laws. Your personal
                information is never shared with third parties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Community & Testimonials Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="mb-8">
                <div className="text-4xl font-bold text-orange-600 mb-2">
                  {pollsterCount.toLocaleString()}+
                </div>
                <div className="text-lg text-muted-foreground">
                  Pollsters worldwide and growing
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <Users className="h-8 w-8 text-orange-600" />
                    </div>
                    <blockquote className="text-muted-foreground italic mb-4">
                      "I've redeemed $50 in gift cards just by sharing my
                      opinions during my commute!"
                    </blockquote>
                    <div className="font-semibold">Sarah M.</div>
                    <div className="text-sm text-muted-foreground">
                      Verified Pollster since 2023
                    </div>
                  </CardContent>
                </Card>

                <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <Star className="h-8 w-8 text-blue-600" />
                    </div>
                    <blockquote className="text-muted-foreground italic mb-4">
                      "Love that my voice actually influences real policy
                      decisions. Plus the rewards are great!"
                    </blockquote>
                    <div className="font-semibold">Michael T.</div>
                    <div className="text-sm text-muted-foreground">
                      Top Contributor
                    </div>
                  </CardContent>
                </Card>

                <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <Heart className="h-8 w-8 text-green-600" />
                    </div>
                    <blockquote className="text-muted-foreground italic mb-4">
                      "Easy to use, quick surveys, and I donated points to my
                      favorite charity. Win-win!"
                    </blockquote>
                    <div className="font-semibold">Jessica L.</div>
                    <div className="text-sm text-muted-foreground">
                      Community Champion
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-amber-400 via-orange-500 to-amber-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Start Earning Points Today
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join over {pollsterCount.toLocaleString()} verified pollsters
              making their voices heard while earning real rewards. Your opinion
              matters—and now it pays.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 py-4 bg-white text-orange-600 hover:bg-white/90 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                onClick={() => (window.location.href = "/verification-process")}
              >
                Register Now - It's Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-4 border-white text-white hover:bg-white/10 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
              >
                See All Rewards
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default VerifiedPollostersLanding;
